import { useState, useMemo, useEffect } from 'react';
import { Search, ChevronRight, ArrowLeft, Sparkles, BookOpen, LifeBuoy, Printer } from 'lucide-react';
import { getHelpArticles } from '../api.js';
import { videoEmbedUrl } from '../videoEmbed.js';
import { assetUrl } from '../assetUrl.js';
import { HELP_CATEGORIES as CATEGORIES, HELP_ARTICLES as DEFAULTS } from './helpDefaults.js';

// In-app Help Center (MoeGo-style). Category cards → article list → reader,
// with search. Content is loaded from the backend (editable by the platform
// owner in Platform Admin → Centro de Ayuda); if nothing is saved yet it falls
// back to the bilingual DEFAULTS so it's never empty.
//
// Three ways to read the same article: on screen, with its screenshot or
// tutorial video, or printed on paper (the 🖨️ buttons) — an agent training a
// new assistant asked for something they could hand over, and "Guardar como
// PDF" from the print dialog is that something.

// Tiny markdown-lite renderer: ### subheading, - bullets, **bold**, numbered
// lines, ![alt](url) media, blank-line paragraphs.
//
// Media uses ONE syntax for images AND video — `![alt](url)` — rather than
// inventing a second marker for video: the platform owner only has to remember
// one button ("Insert media") in the Platform Admin editor, and the renderer
// decides what to draw from the URL itself:
//
//   youtube.com / vimeo.com  → <iframe> (see ../videoEmbed.js)
//   …/algo.mp4 | .webm       → <video>  (what uploadHelpMedia() mints)
//   anything else            → <img>
//
// A URL with no scheme (`ayuda/clients-es.webp`) is a file that ships INSIDE
// the app, under crm-app/public/. It is resolved against BASE_URL so the same
// article works at app.deskte.com AND at deskte.com/demo/app/, which is served
// from a sub-path. Absolute URLs are left alone — that is what uploadHelpMedia()
// returns, and what a YouTube link looks like.
const MEDIA_LINE = /^!\[([^\]]*)\]\((\S+)\)$/;
const VIDEO_EXT = /\.(mp4|webm)(?:[?#]|$)/i;
const ABSOLUTE = /^(https?:|data:|blob:)/i;

export function mediaSrc(url) {
  const s = String(url || '');
  if (ABSOLUTE.test(s)) return s;
  return assetUrl(s);
}

// Body text → a neutral list of blocks. TWO renderers consume this (React,
// below, and the printable HTML further down); parsing once is what keeps the
// printed page and the screen from drifting apart.
export function parseBody(text) {
  const blocks = [];
  let list = null;
  const closeList = () => { if (list) { blocks.push(list); list = null; } };
  String(text || '').split('\n').forEach((raw) => {
    const l = raw.trimEnd();
    const media = MEDIA_LINE.exec(l);
    if (l.startsWith('### ')) { closeList(); blocks.push({ t: 'h3', text: l.slice(4) }); }
    else if (media) { closeList(); blocks.push({ t: 'media', alt: media[1], url: media[2] }); }
    else if (l.startsWith('- ')) { if (!list) list = { t: 'ul', items: [] }; list.items.push({ text: l.slice(2), ordered: false }); }
    else if (/^\d+\.\s/.test(l)) { if (!list) list = { t: 'ul', items: [] }; list.items.push({ text: l.replace(/^\d+\.\s/, ''), ordered: true }); }
    else if (l === '') { closeList(); }
    else { closeList(); blocks.push({ t: 'p', text: l }); }
  });
  closeList();
  return blocks;
}

// Named export (in addition to the default HelpCenterView export below) only
// so this parsing logic is independently verifiable — the project has no
// frontend test harness, so this is what a smoke check imports.
export function renderBody(text) {
  const inline = (s) => String(s).split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith('**') && p.endsWith('**')
    ? <strong key={i} style={{ color: 'var(--c-white,#eef6ff)' }}>{p.slice(2, -2)}</strong>
    : <span key={i}>{p}</span>);
  return parseBody(text).map((b, i) => {
    if (b.t === 'h3') return <div key={i} style={{ fontWeight: 800, fontSize: '.86rem', color: 'var(--c-cyan,#00d4ff)', margin: '.7rem 0 .3rem' }}>{inline(b.text)}</div>;
    if (b.t === 'p') return <p key={i} style={{ fontSize: '.83rem', color: 'var(--c-text,#c8dff0)', lineHeight: 1.6, margin: '.2rem 0' }}>{inline(b.text)}</p>;
    // `display:block`, no flex: un <ul> en flex NO pinta las viñetas, y con
    // artículos de veinte líneas una lista sin viñeta se lee como un párrafo
    // partido.
    if (b.t === 'ul') return (
      <ul key={i} style={{ margin: '.3rem 0 .6rem', paddingLeft: '1.2rem', display: 'block' }}>
        {b.items.map((it, j) => <li key={j} style={{ fontSize: '.82rem', color: 'var(--c-text,#c8dff0)', lineHeight: 1.55, marginBottom: 3, listStyle: it.ordered ? 'decimal' : 'disc' }}>{inline(it.text)}</li>)}
      </ul>
    );
    const embed = videoEmbedUrl(b.url);
    if (embed) {
      // The public demo's CSP has no youtube/vimeo in frame-src, on purpose —
      // it never reaches the backend, so it only ever shows the DEFAULTS, and
      // those carry no external video. Keep it that way.
      return <iframe key={i} src={embed} title={b.alt || 'video'} allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture; fullscreen" allowFullScreen
        style={{ width: '100%', maxWidth: 640, aspectRatio: '16 / 9', border: 0, borderRadius: 10, margin: '.6rem 0', display: 'block', background: '#000' }} />;
    }
    const src = mediaSrc(b.url);
    return VIDEO_EXT.test(b.url)
      ? <video key={i} src={src} controls playsInline preload="metadata" style={{ width: '100%', maxWidth: 640, borderRadius: 10, margin: '.6rem 0', display: 'block', background: '#000' }} />
      : <img key={i} src={src} alt={b.alt} loading="lazy" style={{ maxWidth: '100%', borderRadius: 10, margin: '.6rem 0', display: 'block', border: '1px solid var(--c-border,#1a3550)' }} />;
  });
}

// ── Paper ────────────────────────────────────────────────────────────────────
// One article, or the whole guide, as something you can hand to a new
// assistant. "Guardar como PDF" in the browser's print dialog is the PDF path;
// there is no library to add for that.

function esc(v) {
  if (v === null || v === undefined) return '';
  return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// CSP NOTE — same trap as the client ficha printer (ClientDetail.jsx): the
// print window opens about:blank and INHERITS the SPA's CSP, where
// `style-src-elem 'self'` silently DROPS any <style> block while
// `style-src-attr 'unsafe-inline'` allows inline style="…" attributes. So every
// rule below lives in an attribute. No @media print, no classes.
const P = {
  body: "font-family:'Segoe UI',system-ui,sans-serif;color:#1e293b;padding:30px 38px;line-height:1.55;font-size:13px",
  h1: 'font-size:20px;font-weight:800;margin:0 0 2px;color:#0f172a',
  head: 'display:flex;align-items:flex-start;justify-content:space-between;border-bottom:3px solid #0e7490;padding-bottom:12px;margin-bottom:18px',
  cat: 'font-size:15px;font-weight:800;color:#0e7490;margin:26px 0 8px;padding-bottom:4px;border-bottom:1px solid #cbd5e1',
  art: 'break-inside:avoid;margin:0 0 18px',
  h2: 'font-size:14px;font-weight:800;color:#0f172a;margin:0 0 6px',
  h3: 'font-size:12px;font-weight:800;color:#0e7490;margin:10px 0 3px',
  p: 'font-size:12px;margin:3px 0;color:#334155',
  ul: 'margin:4px 0 8px;padding-left:20px',
  li: 'font-size:12px;margin:2px 0;color:#334155',
  img: 'max-width:100%;border:1px solid #cbd5e1;border-radius:6px;margin:8px 0;display:block',
  link: 'font-size:11px;color:#0e7490;margin:6px 0;display:block;word-break:break-all',
  foot: 'margin-top:24px;padding-top:8px;border-top:1px solid #94a3b8;font-size:9px;color:#94a3b8;display:flex;justify-content:space-between',
};

const inlineHtml = (s) => esc(s).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

// Absolute URL: the print window has no base of its own, so `ayuda/x.webp`
// would resolve against about:blank and never load.
const absUrl = (u) => { try { return new URL(mediaSrc(u), window.location.href).href; } catch { return mediaSrc(u); } };

function bodyToHtml(text, es) {
  return parseBody(text).map((b) => {
    if (b.t === 'h3') return `<div style="${P.h3}">${inlineHtml(b.text)}</div>`;
    if (b.t === 'p') return `<p style="${P.p}">${inlineHtml(b.text)}</p>`;
    if (b.t === 'ul') return `<ul style="${P.ul}">${b.items.map((it) => `<li style="${P.li}">${inlineHtml(it.text)}</li>`).join('')}</ul>`;
    // Paper can't play a video: print the link so the reader can type it.
    if (videoEmbedUrl(b.url) || VIDEO_EXT.test(b.url)) {
      return `<div style="${P.link}">🎬 ${es ? 'Video' : 'Video'}: ${esc(absUrl(b.url))}</div>`;
    }
    return `<img src="${esc(absUrl(b.url))}" alt="${esc(b.alt)}" style="${P.img}">`;
  }).join('');
}

/**
 * @param {Array} articles artículos a imprimir (uno, o todos)
 * @param {Array} categories HELP_CATEGORIES, para los títulos de sección
 * @param {string} lang 'es' | 'en'
 * @param {string} titulo encabezado del documento
 */
export function printHelp(articles, categories, lang, titulo) {
  if (typeof window === 'undefined') return;
  const es = lang === 'es';
  const tt = (o) => (o ? (es ? o.es : o.en) || o.es || o.en : '') || '';
  const fecha = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const w = window.open('', '_blank', 'width=900,height=700');
  if (!w) return;
  // Defense in depth: severs window.opener so the print window cannot reach the
  // parent's __deskteAuth bearer. Article text is platform-owner-authored, but
  // it still travels through the DB and is escaped above either way.
  try { w.opener = null; } catch { /* cross-origin guard, nothing to do */ }

  const grupos = categories
    .map((c) => ({ c, items: articles.filter((a) => a.cat === c.id) }))
    .filter((g) => g.items.length);
  // An article whose category was deleted still has to print, or the "whole
  // guide" button would quietly drop it.
  const huerfanos = articles.filter((a) => !categories.some((c) => c.id === a.cat));
  if (huerfanos.length) grupos.push({ c: { id: '_', title: { es: 'Otros', en: 'Other' }, icon: '' }, items: huerfanos });

  const seccion = (g) => `<div style="${P.cat}">${esc(g.c.icon || '')} ${esc(tt(g.c.title))}</div>` + g.items.map((a) =>
    `<div style="${P.art}"><div style="${P.h2}">${esc(tt(a.title))}</div>${bodyToHtml((es ? a.body?.es : a.body?.en) || a.body?.es || a.body?.en || '', es)}</div>`).join('');

  const html = `<!DOCTYPE html><html lang="${es ? 'es' : 'en'}"><head><meta charset="utf-8"><title>${esc(titulo)}</title></head>`
    + `<body style="${P.body}">`
    + `<div style="${P.head}"><div><div style="${P.h1}">Deskte</div><div style="font-size:13px;color:#475569">${esc(titulo)}</div></div>`
    + `<div style="text-align:right;font-size:11px;color:#64748b">${es ? 'Impreso' : 'Printed'}: ${esc(fecha)}</div></div>`
    + (articles.length > 1 ? grupos.map(seccion).join('') : articles.map((a) =>
      `<div style="${P.art}"><div style="${P.h2}">${esc(tt(a.title))}</div>${bodyToHtml((es ? a.body?.es : a.body?.en) || a.body?.es || a.body?.en || '', es)}</div>`).join(''))
    + `<div style="${P.foot}"><span>${es ? 'Centro de Ayuda de Deskte' : 'Deskte Help Center'}</span><span>app.deskte.com</span></div>`
    + '</body></html>';

  w.document.write(html);
  w.document.close();
  // Give the screenshots a moment to load, or the printed page comes out with
  // empty boxes where the pictures should be.
  setTimeout(() => w.print(), 900);
}

export default function HelpCenterView({ lang, onAskAI, onTickets }) {
  const es = lang === 'es';
  const [cat, setCat] = useState(null);
  const [article, setArticle] = useState(null);
  const [q, setQ] = useState('');
  const [articles, setArticles] = useState(DEFAULTS);

  // Load editable content from the backend; fall back to defaults when empty.
  useEffect(() => {
    let alive = true;
    getHelpArticles()
      .then((data) => { if (alive && Array.isArray(data) && data.length) setArticles(data); })
      .catch(() => { /* keep defaults */ });
    return () => { alive = false; };
  }, []);

  // Bilingual helpers — title/desc are {es,en}; body is {es,en}.
  const tt = (o) => (o ? (es ? o.es : o.en) || o.es || o.en : '') || '';
  const bodyText = (a) => (es ? a.body?.es : a.body?.en) || a.body?.es || a.body?.en || '';

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return null;
    return articles.filter(a => (tt(a.title) + ' ' + (a.body?.es || '') + ' ' + (a.body?.en || '')).toLowerCase().includes(query));
  }, [q, es, articles]);

  const catArticles = (id) => articles.filter(a => a.cat === id);

  const printBtn = (onClick, label) => (
    <button type="button" onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', padding: '.5rem .8rem', borderRadius: 9, fontWeight: 700, fontSize: '.78rem', cursor: 'pointer', fontFamily: 'inherit' }}>
      <Printer size={13} /> {label}
    </button>
  );

  const ArticleCard = ({ a }) => (
    <button type="button" onClick={() => setArticle(a)}
      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.7rem .85rem', cursor: 'pointer', fontFamily: 'inherit' }}>
      <BookOpen size={15} style={{ color: 'var(--c-cyan,#00d4ff)', flexShrink: 0 }} />
      <span style={{ flex: 1, fontSize: '.85rem', fontWeight: 600, color: 'var(--c-white,#eef6ff)' }}>{tt(a.title)}</span>
      <ChevronRight size={15} style={{ color: 'var(--c-text3,#45688a)' }} />
    </button>
  );

  // Footer help actions: ask the AI, or open a support ticket.
  const helpActions = (
    <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
      <button type="button" onClick={() => onAskAI && onAskAI()}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#7c3aed,#5b21b6)', border: 'none', color: '#fff', padding: '.55rem .9rem', borderRadius: 9, fontWeight: 700, fontSize: '.82rem', cursor: 'pointer', fontFamily: 'inherit' }}>
        <Sparkles size={14} /> {es ? 'Preguntar a la IA' : 'Ask the AI'}
      </button>
      <button type="button" onClick={() => onTickets && onTickets()}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-cyan,#00d4ff)', padding: '.55rem .9rem', borderRadius: 9, fontWeight: 700, fontSize: '.82rem', cursor: 'pointer', fontFamily: 'inherit' }}>
        <LifeBuoy size={14} /> {es ? 'Crear ticket / Soporte' : 'Create ticket / Support'}
      </button>
    </div>
  );

  // ── Article reader ──
  if (article) {
    return (
      <div style={{ maxWidth: 720 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setArticle(null)} className="dkt-btn-g">
            <ArrowLeft size={13} /> {es ? 'Volver' : 'Back'}
          </button>
          {printBtn(() => printHelp([article], CATEGORIES, lang, tt(article.title)), es ? 'Imprimir / PDF' : 'Print / PDF')}
        </div>
        <div className="dkt-card" style={{ padding: '1.3rem 1.4rem' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', margin: '0 0 .6rem' }}>{tt(article.title)}</h2>
          <div>{renderBody(bodyText(article))}</div>
          <div style={{ marginTop: '1.2rem', paddingTop: '1rem', borderTop: '1px solid var(--c-border,#1a3550)' }}>
            <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)', marginBottom: '.5rem' }}>{es ? '¿No resolvió tu duda?' : "Didn't solve it?"}</div>
            {helpActions}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.4rem' }}>
        <BookOpen size={20} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
        <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', margin: 0 }}>{es ? 'Centro de Ayuda' : 'Help Center'}</h2>
      </div>
      <div style={{ fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)', marginBottom: '1rem' }}>{es ? 'Una guía por cada módulo del menú, con fotos de la pantalla real. Imprímela para tu equipo, o pregúntale a la IA si algo no está aquí.' : 'A guide for every module in the menu, with real screenshots. Print it for your team, or ask the AI if something is missing.'}</div>

      {/* Search */}
      <div style={{ display: 'flex', gap: '.6rem', alignItems: 'center', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 320px', maxWidth: 560 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text3,#45688a)' }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={es ? 'Buscar artículos…' : 'Search for articles…'}
            style={{ width: '100%', padding: '.7rem .7rem .7rem 2.2rem', borderRadius: 10, border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', fontSize: '.9rem', fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
        {printBtn(() => printHelp(articles, CATEGORIES, lang, es ? 'Manual completo' : 'Complete manual'), es ? 'Imprimir manual completo' : 'Print the whole manual')}
      </div>

      {results !== null ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
          <div style={{ fontSize: '.75rem', color: 'var(--c-text3,#45688a)', marginBottom: '.2rem' }}>{results.length} {es ? 'resultado(s)' : 'result(s)'}</div>
          {results.map(a => <ArticleCard key={a.id} a={a} />)}
          {results.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--c-text3,#45688a)', fontSize: '.85rem' }}>
              <div style={{ marginBottom: '.7rem' }}>{es ? 'Nada por aquí. Prueba con la IA o un ticket:' : 'Nothing here. Try the AI or a ticket:'}</div>
              <div style={{ display: 'inline-flex' }}>{helpActions}</div>
            </div>
          )}
        </div>
      ) : cat ? (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.9rem', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setCat(null)} className="dkt-btn-g">
              <ArrowLeft size={13} /> {es ? 'Todas las categorías' : 'All categories'}
            </button>
            {catArticles(cat).length > 0 && printBtn(
              () => printHelp(catArticles(cat), CATEGORIES, lang, tt(CATEGORIES.find(c => c.id === cat)?.title)),
              es ? 'Imprimir esta sección' : 'Print this section')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.8rem' }}>
            <span style={{ fontSize: '1.3rem' }}>{CATEGORIES.find(c => c.id === cat)?.icon}</span>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', margin: 0 }}>{tt(CATEGORIES.find(c => c.id === cat)?.title)}</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            {catArticles(cat).map(a => <ArticleCard key={a.id} a={a} />)}
            {catArticles(cat).length === 0 && <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.82rem', padding: '.5rem' }}>{es ? 'Sin artículos en esta categoría.' : 'No articles in this category.'}</div>}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: '.9rem' }}>
          {CATEGORIES.map(c => {
            const n = catArticles(c.id).length;
            return (
              <button key={c.id} type="button" onClick={() => setCat(c.id)}
                style={{ textAlign: 'left', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '1.1rem', cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color .15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,212,255,.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--c-border,#1a3550)'; }}>
                <div style={{ fontSize: '1.8rem', marginBottom: '.5rem' }}>{c.icon}</div>
                <div style={{ fontSize: '.95rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', marginBottom: '.25rem' }}>{tt(c.title)}</div>
                <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5, marginBottom: '.6rem' }}>{tt(c.desc)}</div>
                <div style={{ fontSize: '.72rem', color: 'var(--c-cyan,#00d4ff)', fontWeight: 700 }}>{n} {es ? 'artículos' : 'articles'} ›</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Always-visible help footer so "Create ticket" is reachable from the
          landing too (not only inside an article). */}
      <div style={{ marginTop: '1.4rem', paddingTop: '1rem', borderTop: '1px solid var(--c-border,#1a3550)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.7rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '.82rem', color: 'var(--c-text2,#7aa3c0)' }}>{es ? '¿No encuentras lo que buscas?' : "Can't find what you need?"}</span>
        {helpActions}
      </div>
    </div>
  );
}
